import cx from 'classnames';
import {NextPage} from 'next';

import {AnimatedText, Screen} from '#component/base';
import {LifestyleEmpty, LifestyleHeader} from '#component/page/LifestyleSummary/component';
import {ImageCTACard, TallyExplorerCTACard} from '#component/partial';
import {LifestylePreviewCard} from '#component/partial';
import {LearnMoreCarousel} from '#component/partial/LearnMoreCarousel';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {Transition} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {headerTransition} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';
import {getCategorySatisfactionSummary, getNormalizedLifestyleScore} from '#util/lifestyleScore';

import {useLifestyleSummary} from './useLifestyleSummary';

import * as css from './LifestyleSummary.css';

export const LifestyleSummaryScreen = () => {
  const {
    categories,
    categoriesMeetingRequirementsTitles,
    categoriesNotMeetingRequirementsTitles,
    hasActiveSubscription,
    hasMembership,
    hasCompletedSurveyForMostRecentLabSubmissionWithResults,
    hasSelectedActionPlan,
    learningResources,
    lifestyleScore,
    loading,
    maxLifestyleScore,
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
    showLifestylePreview,
    surveyCategories,
    surveyId,
    surveyKey,
  } = useLifestyleSummary();
  const {track} = useAnalytics();
  const mostRecentSurveyAssessment =
    mostRecentLabSubmission?.userSurveySubmission?.surveyAssessment;
  const showFullResults =
    hasActiveSubscription && hasCompletedSurveyForMostRecentLabSubmissionWithResults;
  const satisfactionSummary = getCategorySatisfactionSummary(
    categoriesMeetingRequirementsTitles,
    categoriesNotMeetingRequirementsTitles,
  );

  const handleCTAClick = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: `View gap lifestyle CTA`,
    });
  };

  const handleCardClick = (url: string) => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'View ls carousel content',
      data: {contentSlug: url},
    });
  };

  if (!showFullResults) {
    return (
      <Screen loading={loading} pageName="Lifestyle Summary Empty">
        <LifestyleEmpty
          categories={surveyCategories}
          hasActiveSubscription={hasActiveSubscription}
          hasLabSubmission={!!mostRecentLabSubmission}
          registeredDate={mostRecentLabSubmission?.registeredDate}
          surveyAssessment={mostRecentSurveyAssessment}
        />
      </Screen>
    );
  }

  return (
    <Screen
      contentContainerClassName={css.root}
      withDefaultSizing={false}
      pageName="Lifestyle Summary"
      pageData={{
        surveyId,
        surveyKey,
        surveyScore: lifestyleScore,
        categoryScores: categories.reduce((acc, category) => {
          const score = getNormalizedLifestyleScore(
            category.categoryScore,
            category.maxCategoryScore,
          );
          const label = `${category.category.title}Score`;
          return {...acc, [label]: score};
        }, {}),
      }}
      loading={loading}>
      <LifestyleHeader
        score={lifestyleScore}
        registeredDate={mostRecentLabSubmissionWithResults?.registeredDate}
        maxScore={maxLifestyleScore}
        showLifestylePreview={showLifestylePreview}
      />

      <Transition
        appear
        show
        className={cx(
          css.content,
          sprinkles({dir: 'col', flex: 'flex', gap: [32, 64, 96]}),
          headerTransition,
        )}>
        {hasMembership && (
          <div className={sprinkles({flex: 'flex', dir: 'col', gap: [32, 48, 72]})}>
            <div
              className={cx(
                css.primaryGrid,
                sprinkles({flex: 'flex', dir: 'col', gap: [20, 28, 36]}),
              )}>
              <AnimatedText color="deepBurgundy" splitText={false} variant="heading2">
                Check Out Your Action Plan
              </AnimatedText>
              {satisfactionSummary && (
                <AnimatedText color="doveGray" splitText={false} variant="bodyLarge">
                  {satisfactionSummary}
                </AnimatedText>
              )}
              <AnimatedText color="doveGray" splitText={false} variant="body">
                Discover personalized lifestyle recommendations based on your unique DNA methylation
                to help you lower your TallyAge™.
              </AnimatedText>
            </div>
          </div>
        )}

        {!hasSelectedActionPlan && hasMembership && (
          <div className={css.primaryGrid}>
            <ImageCTACard
              onClick={handleCTAClick}
              href="/actionplan"
              imageUrl="/image/action-plan.jpg"
              headline="Get your Action Plan"
              lede="Get a personalized, actionable checklist of lifestyle changes to improve your TallyAge™ and live better, longer."
              imageClassName={css.actionPlanImage}
            />
          </div>
        )}
        {!hasMembership && (
          <div className={css.primaryGrid}>
            <ImageCTACard
              onClick={handleCTAClick}
              href="/profile?tab=manage"
              headline="Become a Member"
              lede="Become a Tally Health™ member today, discover your epigenetic age, and get science-backed tools to unlock additional years of health and vitality."
              imageClassName={css.actionPlanImage}
              className={css.becomeAMemberBg}
            />
          </div>
        )}

        <div className={css.primaryGrid}>
          <TallyExplorerCTACard variant="lifestylesummary" href="/tallyexplorer/lifestylesummary" />
        </div>

        {showLifestylePreview && (
          <div className={css.primaryGrid}>
            <LifestylePreviewCard
              hasActiveSubscription={hasActiveSubscription}
              lifestyleScore={getNormalizedLifestyleScore(
                mostRecentLabSubmission?.userSurveySubmission?.surveyAssessment?.lifestyleScore ||
                  0,
                mostRecentLabSubmission?.userSurveySubmission?.surveyAssessment
                  ?.maxLifestyleScore || 100,
              )}
              registeredDate={mostRecentLabSubmission?.registeredDate}
            />
          </div>
        )}
      </Transition>
      <LearnMoreCarousel
        onCardClick={handleCardClick}
        learningResources={learningResources}
        headerClassName={css.primaryGrid}
      />
    </Screen>
  );
};

export const LifestyleSummary: NextPage = withRequireUserState(LifestyleSummaryScreen, {
  userState: 'verified',
});
